import { AfterViewInit, Component, HostListener, inject, Renderer2 } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MenuController, NavController, ToastController } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { TranslateService } from '@ngx-translate/core';
import { WebsocketService } from './services/wss.service';
import { AccountRobotsService } from './services/account-robots.service';
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements AfterViewInit {
  public appPages = [
    { title: 'sidebar.tasks', url: '/folder/tasks', icon: 'clipboard' },
    // Solo se muestra si la cuenta tiene más de un robot
    { title: 'sidebar.robots', url: '/folder/robots', icon: 'happy', requiresManyRobots: true },
    { title: 'sidebar.map', url: '/folder/map', icon: 'map' },
    { title: 'sidebar.audios', url: '/folder/audios', icon: 'musical-notes' },
    { title: 'sidebar.actions', url: '/folder/actions', icon: 'game-controller' },
    // Oculta por ahora (sin terminar)
    { title: 'sidebar.remote-control', url: '/folder/remote-control', icon: 'navigate', hidden: true },
  ];
  public labels = [];
  protected readonly languages = [
    { code: 'es', label: 'Español' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' },
  ];

  protected resizing: boolean = false;
  private startX = 0;
  private startWidth = 0;
  private menuEl!: HTMLElement;
  protected currentRoute: string = '';
  protected user: { active: boolean; email: string; role: string; _id: string } | null = null;
  // Serie del robot cuando la cuenta solo tiene uno (se muestra en la tarjeta de Pamela)
  protected robotSerie: string | null = null;
  protected hasManyRobots: boolean = false;

  constructor(
    private renderer: Renderer2,
    private storage: Storage,
    private navController: NavController,
    private router: Router,
    private menuController: MenuController,
    protected translate: TranslateService,
    private accountRobots: AccountRobotsService,
  ) {
    this.translate.addLangs(this.languages.map((l) => l.code));
    // Idioma del navegador mientras se carga el Storage; si hay uno guardado, se aplica en ngAfterViewInit
    this.translate.use(this.getBrowserLang());
  }

  // Primer idioma soportado de la lista de preferencias del navegador (no del sistema operativo)
  private getBrowserLang(): string {
    const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];
    const supported = this.translate.getLangs();
    return preferred.map((l) => l.split(/[-_]/)[0].toLowerCase()).find((l) => supported.includes(l)) ?? 'en';
  }

  protected async changeLanguage(lang: string) {
    this.translate.use(lang);
    await this.storage.set('lang', lang);
  }

  async ngAfterViewInit() {
    this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.url;
        if (this.currentRoute !== '/login' && !this.user) {
          this.user = await this.storage.get('user');
          if (!this.menuController.isEnabled) this.menuController.enable(true);
          this.loadRobots();
        }
      }
    });

    await this.storage.create();
    const savedLang = await this.storage.get('lang');
    if (this.translate.getLangs().includes(savedLang)) this.translate.use(savedLang);

    const token = await this.storage.get('token');
    this.user = await this.storage.get('user');

    if (!token) this.navController.navigateRoot('login');
    else this.loadRobots();
    this.menuEl = document.querySelector('ion-menu')!;
  }

  private async loadRobots() {
    try {
      const series = await this.accountRobots.getSeries();
      this.robotSerie = series.length === 1 ? series[0] : null;
      this.hasManyRobots = series.length > 1;
    } catch (error) {
      console.error(error);
    }
  }

  startResize(event: MouseEvent) {
    this.resizing = true;
    this.startX = event.clientX;
    this.startWidth = this.menuEl.offsetWidth;
    document.body.style.cursor = 'grabbing';
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this.resizing) return;
    const diff = event.clientX - this.startX;
    const newWidth = this.startWidth + diff;
    // límite mínimo/máximo
    if (newWidth > 150 && newWidth < 600) {
      this.renderer.setStyle(this.menuEl, 'width', `${newWidth}px`);
    }
  }

  @HostListener('document:mouseup')
  stopResize() {
    this.resizing = false;
    document.body.style.cursor = '';
  }

  public static raiseToastWithMessage(toastController: ToastController, message: string, duration: number = 3000) {
    toastController
      .create({
        mode: 'ios',
        message: message,
        duration: duration,
        buttons: [{ role: 'cancel', icon: 'close' }],
      })
      .then((toast) => toast.present())
      .catch((error) => {
        console.error(error);
      });
  }

  protected async logout() {
    // El idioma elegido no depende de la sesión, se conserva tras limpiar el Storage
    const lang = await this.storage.get('lang');
    await this.storage.clear();
    if (lang) await this.storage.set('lang', lang);
    localStorage.clear();
    // Para que al entrar con otra cuenta se carguen su usuario y sus robots
    this.user = null;
    this.robotSerie = null;
    this.hasManyRobots = false;
    this.accountRobots.clear();
    this.router.navigate(['/login']);
    this.menuController.enable(false);
  }
}
